import { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Route, Shield, Gem, LifeBuoy, Map, LogOut,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { checkSession, adminLogout } from "@/admin/api";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", testId: "admin-nav-dashboard" },
  { to: "/admin/users", icon: Users, label: "User Management", testId: "admin-nav-users" },
  { to: "/admin/convoys", icon: Route, label: "Convoy Oversight", testId: "admin-nav-convoys" },
  { to: "/admin/clubs", icon: Shield, label: "Club Management", testId: "admin-nav-clubs" },
  { to: "/admin/membership", icon: Gem, label: "Membership & Revenue", testId: "admin-nav-membership" },
  { to: "/admin/support", icon: LifeBuoy, label: "Support", testId: "admin-nav-support" },
  { to: "/admin/map", icon: Map, label: "Live Ops Map", testId: "admin-nav-map" },
];

export default function AdminLayout() {
  const [state, setState] = useState("checking");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkSession().then((ok) => setState(ok ? "authed" : "guest"));
  }, [location.pathname]);

  const logout = async () => {
    await adminLogout();
    navigate("/admin/login");
  };

  if (state === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]" data-testid="admin-loading">
        <p className="label-tech text-neutral-500">Checking session…</p>
      </div>
    );
  }
  if (state === "guest") return <Navigate to="/admin/login" replace />;

  return (
    <div className="flex min-h-screen bg-[#050505]" data-testid="admin-portal">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-white/10 bg-[#0a0a0c] lg:flex">
        <div className="border-b border-white/10 px-5 py-4">
          <Logo imgClassName="h-11 w-auto" />
          <p className="label-tech mt-1 text-[9px] text-neutral-500">Admin Portal</p>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3" data-testid="admin-sidebar">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "border border-[#FF3333]/30 bg-[#FF3333]/10 text-white"
                    : "border border-transparent text-neutral-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <button
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-400 transition-colors duration-200 hover:bg-white/5 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col lg:pl-60">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/10 bg-[#0a0a0c]/90 px-4 py-3 backdrop-blur-xl lg:hidden">
          <Logo imgClassName="h-9 w-auto" />
          <button
            onClick={logout}
            className="flex items-center gap-2 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-neutral-300"
            data-testid="admin-logout-btn-mobile"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b border-white/10 bg-[#0a0a0c] px-3 py-2 lg:hidden" data-testid="admin-mobile-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold ${
                  isActive ? "bg-[#FF3333]/15 text-white" : "text-neutral-400"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <main className="flex-1 px-4 py-8 sm:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
